import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Package, Monitor, Truck, Wrench, Archive, ShoppingCart, TrendingUp } from 'lucide-react';

const STATUS_COLORS = {
  Available: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  Deployed: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  'Under Repair': 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  Retired: 'bg-gray-500/15 text-gray-400 border-gray-500/30',
  'In Transit': 'bg-purple-500/15 text-purple-400 border-purple-500/30',
  Sold: 'bg-red-500/15 text-red-400 border-red-500/30',
  Active: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
};

const STATUS_ICONS = {
  Available: <Package className="h-5 w-5" />,
  Deployed: <Monitor className="h-5 w-5" />,
  'Under Repair': <Wrench className="h-5 w-5" />,
  Retired: <Archive className="h-5 w-5" />,
  'In Transit': <Truck className="h-5 w-5" />,
  Sold: <ShoppingCart className="h-5 w-5" />,
};

export default function InventoryDashboardPage() {
  const [summary, setSummary] = useState(null);
  const [assets, setAssets] = useState([]);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [sumRes, assetsRes] = await Promise.all([
          api.get('/inventory/summary'),
          api.get('/assets'),
        ]);
        setSummary(sumRes.data);
        setAssets(Array.isArray(assetsRes.data) ? assetsRes.data : []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filtered = assets.filter(a => {
    const s = a.asset_status === 'Active' ? 'Available' : a.asset_status;
    if (filterCategory !== 'all' && a.asset_type !== filterCategory) return false;
    if (filterStatus !== 'all' && s !== filterStatus) return false;
    return true;
  });

  const categories = [...new Set(assets.map(a => a.asset_type))].sort();

  if (loading) return <div className="flex justify-center items-center h-64"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6" data-testid="inventory-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Inventory Dashboard</h1>
          <p className="text-muted-foreground text-sm">Real-time asset status overview</p>
        </div>
        <Badge variant="outline" className="text-lg px-4 py-1">{summary?.total_assets || 0} Total Assets</Badge>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="status-cards">
        {(summary?.by_status || []).map(s => (
          <Card key={s.status} className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => setFilterStatus(filterStatus === s.status ? 'all' : s.status)}>
            <CardContent className="p-4 text-center">
              <div className={`inline-flex items-center justify-center h-10 w-10 rounded-full mb-2 ${STATUS_COLORS[s.status] || 'bg-gray-500/15'}`}>
                {STATUS_ICONS[s.status] || <Package className="h-5 w-5" />}
              </div>
              <div className="text-2xl font-bold">{s.count}</div>
              <div className="text-xs text-muted-foreground">{s.status}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Category Breakdown */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><TrendingUp className="h-4 w-4" /> By Category</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
            {(summary?.by_category || []).map(c => (
              <div key={c.category}
                className={`p-3 rounded-lg border cursor-pointer transition-colors ${filterCategory === c.category ? 'bg-primary/10 border-primary' : 'hover:bg-muted'}`}
                onClick={() => setFilterCategory(filterCategory === c.category ? 'all' : c.category)}
              >
                <div className="font-semibold text-sm">{c.category}</div>
                <div className="text-xl font-bold">{c.count}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex gap-3 items-center">
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="All Categories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {['Available', 'Deployed', 'Under Repair', 'In Transit', 'Retired', 'Sold'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">{filtered.length} assets shown</span>
      </div>

      {/* Asset Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left p-3 font-medium">Asset ID</th>
                  <th className="text-left p-3 font-medium">Type</th>
                  <th className="text-left p-3 font-medium">Brand / Model</th>
                  <th className="text-left p-3 font-medium">Serial</th>
                  <th className="text-left p-3 font-medium">Status</th>
                  <th className="text-left p-3 font-medium">Customer</th>
                  <th className="text-left p-3 font-medium">Rate/mo</th>
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, 100).map(a => {
                  const status = a.asset_status === 'Active' ? 'Available' : a.asset_status;
                  return (
                    <tr key={a.id} className="border-b hover:bg-muted/30 transition-colors">
                      <td className="p-3 font-mono text-xs">{a.asset_id}</td>
                      <td className="p-3">{a.asset_type}</td>
                      <td className="p-3">{a.brand} {a.model_name}</td>
                      <td className="p-3 font-mono text-xs">{a.serial_number}</td>
                      <td className="p-3"><Badge variant="outline" className={STATUS_COLORS[status]}>{status}</Badge></td>
                      <td className="p-3 text-muted-foreground">{a.current_customer || '—'}</td>
                      <td className="p-3">{a.default_monthly_rate ? `₹${a.default_monthly_rate}` : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
